/* ============================================================
   MORFYLO v3 — chambre.js · « La chambre de Morphée »
   Port vanilla (zéro dépendance) des mécaniques du prototype v5 :
   étoiles canvas + étoiles filantes · la nuit tombe au scroll (--night)
   · nuages parallax (scroll + pointeur) · révélations · image mask reveal
   · cartes empilées (desktop) · mots qui s'allument · marquee sur la vague
   · 7 nuits « étoile filante » (comète + pastilles) · anatomie (morph classique ↔ Morfylo) · témoignages animés · « Bonne nuit » manuscrit
   · étoiles au survol + « pouf » au clic · défilement doux (Lenis, si présent).
   Tout est coupé en prefers-reduced-motion. Sans JS, tout reste visible (CSS).
   ============================================================ */
(() => {
  'use strict';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.documentElement;
  const qs = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => Array.from(c.querySelectorAll(s));
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const isDesktop = () => innerWidth > 980;
  const vh = () => innerHeight;

  /* ---------- scroll bus : un seul écouteur, rAF, abonnés ---------- */
  const subs = [];
  let raf = 0;
  const flush = () => { raf = 0; for (const f of subs) f(); };
  const onScroll = () => { if (!raf) raf = requestAnimationFrame(flush); };
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll);
  const sub = (f) => { subs.push(f); f(); };
  /** Progression 0→1 : 0 quand le haut de l'élément atteint startFrac·vh, 1 quand son bas (ou son haut si
      useBottomForEnd = false) atteint endFrac·vh. */
  const progress = (el, startFrac, endFrac, useBottomForEnd = true) => {
    const r = el.getBoundingClientRect();
    const start = vh() * startFrac, end = vh() * endFrac;
    const D = useBottomForEnd ? (start - end + r.height) : (start - end);
    return clamp((start - r.top) / (D || 1), 0, 1);
  };

  /* ---------- Lenis (défilement doux, desktop pointeur fin uniquement) ---------- */
  let lenis = null;
  if (!reduce && window.Lenis && matchMedia('(pointer: fine)').matches && isDesktop()) {
    lenis = new Lenis({ lerp: 0.085, smoothWheel: true });
    window.__lenis = lenis;
    const loop = (t) => { lenis.raf(t); requestAnimationFrame(loop); };
    requestAnimationFrame(loop);
    lenis.on('scroll', onScroll);
  }
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const id = a.getAttribute('href');
    if (id.length < 2) return;
    const el = document.getElementById(id.slice(1));
    if (!el) return;
    e.preventDefault();
    if (lenis) lenis.scrollTo(el, { offset: -80, duration: 1.4 });
    else el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
  });

  /* ---------- étoiles canvas + étoiles filantes ---------- */
  const cv = qs('[data-stars]');
  if (cv && cv.getContext) {
    const ctx = cv.getContext('2d');
    const dpr = Math.min(devicePixelRatio || 1, 2);
    let stars = [], shots = [], W = 0, H = 0;
    const resize = () => {
      W = cv.width = innerWidth * dpr; H = cv.height = innerHeight * dpr;
      const n = innerWidth < 700 ? 70 : 150;
      stars = Array.from({ length: n }, () => ({ x: Math.random() * W, y: Math.random() * H, r: (Math.random() < .85 ? .8 + Math.random() * 1.2 : 2 + Math.random() * 1.4) * dpr, p: Math.random() * Math.PI * 2, s: .6 + Math.random() * 1.2 }));
    };
    resize(); addEventListener('resize', resize);
    let last = 0, nextShot = 4000;
    const draw = (t) => {
      if (document.hidden) { last = t; requestAnimationFrame(draw); return; }
      const dt = Math.min(t - last, 60); last = t;
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#fff';
      for (const s of stars) {
        s.p += dt * .0012 * s.s;
        const a = .35 + .65 * (Math.sin(s.p) * .5 + .5);
        ctx.globalAlpha = a; ctx.beginPath(); ctx.arc(s.x, s.y, s.r * (0.8 + a * .4), 0, Math.PI * 2); ctx.fill();
      }
      nextShot -= dt;
      const night = parseFloat(root.style.getPropertyValue('--night')) || 0;
      if (nextShot <= 0 && !reduce) { nextShot = 5000 + Math.random() * 7000; if (night > .15) shots.push({ x: Math.random() * W * .6, y: Math.random() * H * .35, vx: 1.1 * dpr, vy: .45 * dpr, life: 1 }); }
      for (const sh of shots) {
        sh.x += sh.vx * dt; sh.y += sh.vy * dt; sh.life -= dt / 1300;
        const g = ctx.createLinearGradient(sh.x - 140 * dpr, sh.y - 57 * dpr, sh.x, sh.y);
        g.addColorStop(0, 'rgba(255,255,255,0)'); g.addColorStop(1, `rgba(255,246,227,${Math.max(0, sh.life)})`);
        ctx.strokeStyle = g; ctx.lineWidth = 2 * dpr; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(sh.x - 140 * dpr, sh.y - 57 * dpr); ctx.lineTo(sh.x, sh.y); ctx.stroke();
      }
      shots = shots.filter((s) => s.life > 0);
      ctx.globalAlpha = 1;
      if (!reduce) requestAnimationFrame(draw);
    };
    requestAnimationFrame(draw);
  }

  /* ---------- la nuit tombe (0 → 1 sur la hauteur de la page) ---------- */
  sub(() => {
    const max = document.documentElement.scrollHeight - innerHeight;
    const p = max > 0 ? clamp((scrollY / max) * 1.2, 0, 1) : 0;
    root.style.setProperty('--night', p.toFixed(3));
  });

  /* ---------- nuages : parallax scroll + pointeur ---------- */
  const layers = qsa('.clouds');
  if (layers.length && !reduce) {
    const target = { x: 0, y: 0 }, cur = { x: 0, y: 0 };
    let sy = 0;
    addEventListener('pointermove', (e) => { target.x = e.clientX / innerWidth - .5; target.y = e.clientY / innerHeight - .5; }, { passive: true });
    sub(() => { sy = scrollY; });
    const tick = () => {
      cur.x += (target.x - cur.x) * .06; cur.y += (target.y - cur.y) * .06;
      for (const l of layers) {
        const d = parseFloat(l.dataset.depth) || .2;
        const max = document.documentElement.scrollHeight - innerHeight;
        const sp = max > 0 ? sy / max : 0;
        l.style.transform = `translate3d(${cur.x * -180 * d}px, ${-innerHeight * d * 1.6 * sp}px, 0)`;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* ---------- révélations [data-reveal] (IO + filet de sécurité 3 s) ---------- */
  const reveals = qsa('[data-reveal]');
  if (reveals.length) {
    if (reduce || !('IntersectionObserver' in window)) reveals.forEach((el) => el.classList.add('is-in'));
    else {
      const io = new IntersectionObserver((entries) => { for (const en of entries) if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } }, { threshold: 0, rootMargin: '0px 0px -12% 0px' });
      reveals.forEach((el) => io.observe(el));
      setTimeout(() => reveals.forEach((el) => { const r = el.getBoundingClientRect(); if (r.top < innerHeight) el.classList.add('is-in'); }), 3000);
    }
  }

  /* ---------- Reveal Image Mask (desktop : scrub ; mobile : ouvert) ---------- */
  qsa('.mask-reveal').forEach((el) => {
    const img = el.querySelector('img');
    if (!img || reduce || !isDesktop()) { el.classList.add('is-open'); return; }
    sub(() => {
      const p = progress(el, .85, .45, false);
      img.style.clipPath = `inset(${(18 * (1 - p)).toFixed(2)}% round ${(30 * (1 - p)).toFixed(1)}px)`;
      img.style.transform = `scale(${(1.12 - .12 * p).toFixed(4)})`;
      if (p >= 1) el.classList.add('is-open');
    });
  });

  /* ---------- Stacking Cards (desktop) ---------- */
  const cards = qsa('.stack__card');
  cards.forEach((c, i) => {
    c.style.setProperty('--i', i);
    if (reduce || !isDesktop() || i === cards.length - 1) return;
    const next = cards[i + 1];
    sub(() => {
      const p = progress(next, .8, .3, false);
      const sc = 1 - (cards.length - 1 - i) * .04 * p;
      c.style.transform = `scale(${sc.toFixed(4)})`;
      c.style.filter = p > .05 ? `blur(${(p).toFixed(2)}px)` : 'none';
    });
  });

  /* ---------- Scroll Word Reveal ---------- */
  qsa('[data-words-reveal]').forEach((why) => {
    why.innerHTML = why.textContent.trim().split(/\s+/).map((w) => `<span class="w">${w}</span>`).join(' ');
    const ws = qsa('.w', why);
    if (reduce) { ws.forEach((w) => w.classList.add('is-lit')); return; }
    sub(() => {
      const p = progress(why, .8, .45);
      const n = Math.floor(p * ws.length);
      ws.forEach((w, i) => w.classList.toggle('is-lit', i <= n));
    });
  });

  /* ---------- Marquee le long de la vague (périodique, mesuré) ---------- */
  qsa('[data-wave-text]').forEach((tp) => {
    const unit = (tp.dataset.unit || tp.textContent).trim() + ' ';
    tp.textContent = unit.repeat(12);
    const svgEl = tp.closest('svg');
    const measure = () => { const probe = document.createElementNS('http://www.w3.org/2000/svg', 'text'); probe.setAttribute('class', 'wavequee__text'); probe.textContent = unit; svgEl.appendChild(probe); const L = probe.getComputedTextLength(); probe.remove(); return L || 600; };
    let unitLen = measure(), off = 0, hover = false, visible = true;
    addEventListener('resize', () => { unitLen = measure(); });
    const wv = tp.closest('.wavequee');
    wv.addEventListener('pointerenter', () => hover = true); wv.addEventListener('pointerleave', () => hover = false);
    if ('IntersectionObserver' in window) new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(wv);
    if (!reduce) { const loop = () => { if (visible) { off -= hover ? .35 : 1.1; if (off <= -unitLen) off += unitLen; tp.setAttribute('startOffset', off); } requestAnimationFrame(loop); }; loop(); }
  });

  /* ---------- 7 nuits « étoile filante » : comète canvas + compteur 1→30 + pastilles auto + Momo qui saute de nuit en nuit ---------- */
  const DUST = '<svg class="npills__dust" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 0c1 7 5 11 12 12-7 1-11 5-12 12-1-7-5-11-12-12 7-1 11-5 12-12z"/></svg>';
  qsa('[data-nights]').forEach((sec) => {
    const tabs = qsa('.npills__btn', sec), panels = qsa('.nphrase__p', sec), num = qs('[data-night-num]', sec);
    const cv = qs('[data-comet]', sec), momoWrap = qs('[data-night-momo]', sec), momo = momoWrap && qs('.momo', momoWrap), pw = qs('[data-npills-wrap]', sec);
    if (!tabs.length) return;
    const NIGHTS = tabs.map((b) => parseInt(b.dataset.night, 10) || 1);
    const last = NIGHTS[NIGHTS.length - 1] || 30;
    const interval = parseInt(sec.dataset.interval, 10) || 2200;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    let idx = 0, timer = 0, started = false, shown = 1, numRaf = 0, cur = null;
    const setNum = (target) => {
      cancelAnimationFrame(numRaf);
      if (reduce) { shown = target; num.textContent = target; return; }
      const from = shown, t0 = performance.now(), D = 600;
      const step = (t) => { const p = clamp((t - t0) / D, 0, 1); shown = Math.round(from + (target - from) * easeOut(p)); num.textContent = shown; if (p < 1) numRaf = requestAnimationFrame(step); };
      numRaf = requestAnimationFrame(step);
    };
    /* poussière d'étoiles à l'atterrissage */
    const dust = (x, y) => {
      for (let i = 0; i < 6; i++) {
        const w = document.createElement('span'); w.innerHTML = DUST; const d = w.firstChild;
        const a = Math.PI + (i / 5) * Math.PI, r = 16 + Math.random() * 18;
        d.style.left = x + 'px'; d.style.top = y + 'px';
        d.style.setProperty('--dx', (Math.cos(a) * r).toFixed(0) + 'px'); d.style.setProperty('--dy', (Math.sin(a) * r * .6 - 6).toFixed(0) + 'px');
        d.style.setProperty('--dur', (.55 + Math.random() * .35).toFixed(2) + 's');
        pw.appendChild(d); setTimeout(() => d.remove(), 1000);
      }
    };
    /* Momo posé sur la pastille i ; animate = saut en arc (0,6 s, squash/stretch) depuis la position courante */
    const place = (i, animate) => {
      if (!momoWrap || !pw || !tabs[i]) return;
      const r = tabs[i].getBoundingClientRect(), w = pw.getBoundingClientRect();
      const mw = momoWrap.offsetWidth || 64, mh = momoWrap.offsetHeight || Math.round(mw * 640 / 588);
      const x = r.left - w.left + r.width / 2 - mw / 2, y = r.top - w.top - mh + 6;
      const from = cur || { x, y }; cur = { x, y };
      momoWrap.classList.add('is-placed');
      if (!animate || reduce || !momoWrap.animate) { momoWrap.getAnimations().forEach((an) => an.cancel()); momoWrap.style.transform = `translate(${x}px,${y}px)`; return; }
      const midX = (from.x + x) / 2, top = Math.min(from.y, y) - 48;
      momoWrap.style.transform = '';
      momoWrap.animate([
        { transform: `translate(${from.x}px,${from.y}px) scale(1,1)`, offset: 0 },
        { transform: `translate(${from.x}px,${from.y + 6}px) scale(1.18,.82)`, offset: .14 },
        { transform: `translate(${midX}px,${top}px) scale(.9,1.14)`, offset: .5 },
        { transform: `translate(${x}px,${y}px) scale(1.24,.76)`, offset: .82 },
        { transform: `translate(${x}px,${y - 4}px) scale(.96,1.05)`, offset: .92 },
        { transform: `translate(${x}px,${y}px) scale(1,1)`, offset: 1 },
      ], { duration: 600, easing: 'cubic-bezier(.3,.5,.4,1)', fill: 'forwards' });
      setTimeout(() => dust(x + mw / 2, y + mh - 4), 470);
    };
    const select = (i, user) => {
      const prev = idx;
      idx = (i + tabs.length) % tabs.length;
      tabs.forEach((b, k) => b.setAttribute('aria-selected', k === idx ? 'true' : 'false'));
      panels.forEach((p, k) => p.classList.toggle('is-on', k === idx));
      if (started) setNum(NIGHTS[idx]);
      const asleep = idx === tabs.length - 1;
      place(idx, user !== 'init' && prev !== idx);
      if (momo && window.Momo) {
        if (asleep) setTimeout(() => { if (idx === tabs.length - 1) window.Momo.doze(momo); }, 650);
        else { window.Momo.set(momo, 'awake'); if (idx === 0 && user !== 'init') setTimeout(() => window.Momo.yawn(momo), 650); }
      }
      clearTimeout(timer);
      if (!reduce) timer = setTimeout(() => select(idx + 1), user === true ? interval * 3 : (asleep ? interval * 2 : interval));
    };
    tabs.forEach((b, k) => b.addEventListener('click', () => select(k, true)));
    addEventListener('resize', () => { if (started) place(idx, false); });
    // comète : tête + traînée sur un arc gauche → droite pendant 2 s, en même temps que le compteur 1 → 30
    const comet = () => {
      if (!cv || !cv.getContext || reduce) { setNum(last); setTimeout(() => select(0, 'init'), 300); return; }
      const ctx = cv.getContext('2d'), dpr = Math.min(devicePixelRatio || 1, 2);
      const W = cv.width = cv.clientWidth * dpr, H = cv.height = cv.clientHeight * dpr;
      const pts = [], t0 = performance.now(), D = 2000;
      shown = 1;
      const trail = () => {
        for (let i = 1; i < pts.length; i++) {
          const a = i / pts.length;
          ctx.strokeStyle = `rgba(255,224,178,${a.toFixed(3)})`; ctx.lineWidth = (1.5 + a * 10) * dpr; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(pts[i - 1].x, pts[i - 1].y); ctx.lineTo(pts[i].x, pts[i].y); ctx.stroke();
        }
      };
      const draw = (t) => {
        const p = clamp((t - t0) / D, 0, 1), e = easeOut(p);
        const x = -W * .1 + W * 1.2 * e, y = H * .78 - Math.sin(e * Math.PI) * H * .55;
        pts.push({ x, y, t });
        while (pts.length && t - pts[0].t > 520) pts.shift();
        ctx.clearRect(0, 0, W, H);
        trail();
        ctx.shadowColor = 'rgba(255,217,163,.9)'; ctx.shadowBlur = 24 * dpr;
        const g = ctx.createRadialGradient(x, y, 0, x, y, 18 * dpr);
        g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(.35, 'rgba(255,240,200,.9)'); g.addColorStop(1, 'rgba(255,217,163,0)');
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, 18 * dpr, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
        num.textContent = Math.max(1, Math.round(1 + (last - 1) * e));
        if (p < 1) { requestAnimationFrame(draw); return; }
        shown = last;
        let fade = 1;
        const out = () => { fade -= .06; ctx.clearRect(0, 0, W, H); if (fade > 0) { ctx.globalAlpha = fade; trail(); ctx.globalAlpha = 1; requestAnimationFrame(out); } };
        out();
        setTimeout(() => select(0, 'init'), 350);
      };
      requestAnimationFrame(draw);
    };
    const start = () => { if (started) return; started = true; place(0, false); setTimeout(() => place(idx, false), 700); if (momo && window.Momo) window.Momo.yawn(momo); comet(); };
    // Momo posé sur la 1re pastille dès le chargement (sans saut), le spectacle démarre à l'entrée dans l'écran
    place(0, false);
    if (!('IntersectionObserver' in window)) { start(); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { start(); io.disconnect(); } }, { threshold: .35 });
    io.observe(sec);
    document.addEventListener('visibilitychange', () => { if (document.hidden) clearTimeout(timer); else if (started && !reduce) { clearTimeout(timer); timer = setTimeout(() => select(idx + 1), interval); } });
  });

  /* ---------- Bande science (sceaux) : tap / survol / focus → bulle avec le fait ; marquee en pause tant que la bulle est ouverte ---------- */
  qsa('[data-sci]').forEach((sec) => {
    const band = qs('[data-sci-band]', sec), bubble = qs('[data-sci-bubble]', sec);
    if (!band || !bubble) return;
    const factEl = qs('[data-sci-fact]', bubble), srcEl = qs('[data-sci-src]', bubble);
    let cur = null, hideT = 0;
    const close = () => { bubble.hidden = true; band.classList.remove('is-paused'); if (cur) cur.classList.remove('is-on'); cur = null; };
    const open = (seal) => {
      clearTimeout(hideT);
      if (cur) cur.classList.remove('is-on');
      cur = seal; seal.classList.add('is-on');
      factEl.textContent = seal.dataset.fact || ''; srcEl.textContent = seal.dataset.source ? '— ' + seal.dataset.source : '';
      bubble.hidden = false; band.classList.add('is-paused');
    };
    band.addEventListener('click', (e) => { const seal = e.target.closest('[data-sci-seal]'); if (!seal) return; if (cur === seal && !bubble.hidden) close(); else open(seal); });
    band.addEventListener('pointerover', (e) => { if (e.pointerType !== 'mouse') return; const seal = e.target.closest('[data-sci-seal]'); if (seal && seal !== cur) open(seal); });
    band.addEventListener('focusin', (e) => { const seal = e.target.closest('[data-sci-seal]'); if (seal) open(seal); });
    band.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') hideT = setTimeout(close, 900); });
    bubble.addEventListener('pointerenter', () => clearTimeout(hideT));
    bubble.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') hideT = setTimeout(close, 600); });
    qs('[data-sci-close]', bubble)?.addEventListener('click', close);
    document.addEventListener('click', (e) => { if (!bubble.hidden && !sec.contains(e.target)) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !bubble.hidden) close(); });
  });

  /* ---------- Anatomie : progression continue 0 (classique) → 1 (Morfylo) — scrub au défilement + sélecteur (morph 1,4 s) ---------- */
  const lerpStr = (a, b, t) => {
    const na = a.match(/-?\d*\.?\d+/g), nb = b.match(/-?\d*\.?\d+/g);
    if (!na || !nb || na.length !== nb.length) return t < .5 ? a : b;
    let i = 0;
    return b.replace(/-?\d*\.?\d+/g, () => { const v = parseFloat(na[i]) + (parseFloat(nb[i]) - parseFloat(na[i])) * t; i++; return v.toFixed(2); });
  };
  qsa('[data-anatomy]').forEach((sec) => {
    const stage = qs('[data-anat-stage]', sec), toggle = qs('.anat__toggle', sec), texts = qs('.anat__texts', sec);
    const paths = qsa('[data-anat-path], [data-anat-cerv]', sec), head = qs('[data-anat-head]', sec), btns = qsa('[data-anat-set]', sec);
    const cerv = qs('[data-anat-cerv]', sec), beads = qsa('[data-anat-beads] circle', sec);
    const JIT = [0, 2.5, -3.5, 4, -3, 2, 0];
    let t = 1, target = 1, manual = false, raf = 0, shown = null;
    const smooth = (x) => x * x * (3 - 2 * x);
    const headT = (v) => { const [x, y, r] = v.split(' ').map(parseFloat); return `translate(${x} ${y}) rotate(${r})`; };
    const render = (v) => {
      t = clamp(v, 0, 1);
      const p = smooth(t);
      stage.style.setProperty('--t', p.toFixed(3));
      paths.forEach((el) => el.setAttribute('d', lerpStr(el.dataset.classic, el.dataset.morfylo, p)));
      if (head) head.setAttribute('transform', headT(lerpStr(head.dataset.classic, head.dataset.morfylo, p)));
      if (cerv && beads.length && cerv.getTotalLength) {
        const L = cerv.getTotalLength();
        beads.forEach((c, i) => { const pt = cerv.getPointAtLength(L * i / (beads.length - 1)); c.setAttribute('cx', pt.x.toFixed(2)); c.setAttribute('cy', (pt.y + JIT[i] * (1 - p)).toFixed(2)); });
      }
      const state = t < .5 ? 'classic' : 'morfylo';
      if (state !== shown) {
        shown = state;
        [stage, toggle, texts].forEach((el) => { if (el) { el.classList.toggle('is-classic', state === 'classic'); el.classList.toggle('is-morfylo', state === 'morfylo'); } });
        btns.forEach((b) => b.setAttribute('aria-pressed', b.dataset.anatSet === state ? 'true' : 'false'));
      }
    };
    const animateTo = (to) => {
      cancelAnimationFrame(raf); target = to;
      if (reduce) { render(to); return; }
      const from = t, t0 = performance.now(), D = 1400;
      const ease = (x) => x < .5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
      const step = (now) => { const k = ease(clamp((now - t0) / D, 0, 1)); render(from + (to - from) * k); if (k < 1) raf = requestAnimationFrame(step); };
      raf = requestAnimationFrame(step);
    };
    btns.forEach((b) => b.addEventListener('click', () => { manual = true; animateTo(b.dataset.anatSet === 'classic' ? 0 : 1); }));
    render(1);
    // scrub : 0 quand le haut de la scène est à 65 % de l'écran, 1 à 35 % ; le sélecteur prend la main jusqu'à ce que la scène quitte l'écran
    if (sec.dataset.scrub === 'true' && !reduce) {
      sub(() => {
        const r = stage.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh()) { manual = false; return; }
        if (manual) return;
        cancelAnimationFrame(raf);
        render(progress(stage, .65, .35, false));
      });
    }
  });

  /* ---------- Témoignages animés (état vide honnête ou vrais avis) ---------- */
  qsa('[data-tst]').forEach((tst) => {
    const slides = qsa('.tst__slide', tst), imgs = qsa('.tst__img', tst);
    if (!slides.length) return;
    slides.forEach((s) => { const p = s.querySelector('[data-words]'); if (p) p.innerHTML = p.textContent.trim().split(/\s+/).map((w) => `<span class="w">${w}</span>`).join(' '); });
    let ti = 0, timer;
    const show = (i) => {
      ti = (i + slides.length) % slides.length;
      slides.forEach((s, k) => s.classList.toggle('is-on', k === ti));
      imgs.forEach((im, k) => { im.classList.toggle('is-on', k === ti % imgs.length); im.classList.toggle('is-behind', k === (ti + 1) % imgs.length); });
      qsa('.w', slides[ti]).forEach((w, k) => { w.style.transitionDelay = (k * 40) + 'ms'; });
      clearTimeout(timer);
      if (!reduce && slides.length > 1) timer = setTimeout(() => show(ti + 1), 6500);
    };
    qs('[data-tst-prev]', tst)?.addEventListener('click', () => show(ti - 1));
    qs('[data-tst-next]', tst)?.addEventListener('click', () => show(ti + 1));
    show(0);
  });

  /* ---------- « Bonne nuit » manuscrit ---------- */
  qsa('[data-hand]').forEach((el) => {
    if (reduce || !('IntersectionObserver' in window)) { el.classList.add('is-on'); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('is-on'); io.disconnect(); } }, { threshold: .3 });
    io.observe(el);
  });

  /* ---------- Star Button : étoiles au survol + « pouf » au clic ---------- */
  if (!reduce) {
    const starSVG = '<svg class="btn__star" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 0c1 7 5 11 12 12-7 1-11 5-12 12-1-7-5-11-12-12 7-1 11-5 12-12z"/></svg>';
    const spawn = (btn) => {
      const wrap = document.createElement('span'); wrap.innerHTML = starSVG; const el = wrap.firstChild;
      el.style.left = (Math.random() * 100) + '%'; el.style.top = (30 + Math.random() * 60) + '%';
      el.style.setProperty('--dx', ((Math.random() - .5) * 30) + 'px'); el.style.setProperty('--dy', (-30 - Math.random() * 30) + 'px'); el.style.setProperty('--sc', (1 + Math.random()).toFixed(2)); el.style.setProperty('--dur', (.9 + Math.random() * .5).toFixed(2) + 's');
      btn.appendChild(el); setTimeout(() => el.remove(), 1500);
    };
    document.addEventListener('pointerenter', (e) => {
      const btn = e.target.closest?.('.btn--stars'); if (!btn || btn.dataset.starsOn) return;
      btn.dataset.starsOn = '1'; spawn(btn);
      const t = setInterval(() => spawn(btn), 160);
      btn.addEventListener('pointerleave', () => { clearInterval(t); delete btn.dataset.starsOn; }, { once: true });
    }, true);
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-puff]'); if (!btn) return;
      const r = btn.getBoundingClientRect(), cx = e.clientX || r.left + r.width / 2, cy = e.clientY || r.top + r.height / 2;
      for (let i = 0; i < 7; i++) {
        const p = document.createElement('span'); p.className = 'puff'; p.style.left = cx + 'px'; p.style.top = cy + 'px';
        const a = (i / 7) * Math.PI * 2 + Math.random() * .6, d = 26 + Math.random() * 30;
        p.style.setProperty('--dx', (Math.cos(a) * d) + 'px'); p.style.setProperty('--dy', (Math.sin(a) * d - 10) + 'px'); p.style.setProperty('--sc', (1.8 + Math.random()).toFixed(2));
        document.body.appendChild(p); setTimeout(() => p.remove(), 900);
      }
    });
  }
})();
